import React, { Fragment, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Helmet from 'react-helmet';
import compose from 'lodash.flowright';
import { mapProps } from 'recompose';
import { withRouter } from 'react-router-dom';
import { renderRoutes } from 'react-router-config';
import { graphql, withApollo } from 'react-apollo';
import { connect } from 'react-redux';
import qs from 'query-string';

import getAccount from '../../../graphql/queries/getAccount';
import Header from './components/Header';
import PurchasedCustomPrintConfigNotice from '../../../../pro/views/HierarchyItem/Matches/components/PurchasedCustomPrintConfigNotice';
import IdentitySelector from './components/IdentitySelector';
import { getSimplifiedAlbumMembershipGroupQuery } from './graphql/queries/getSimplifiedAlbumMembershipGroup';
import { CheckoutCartModal } from '../../../../../services/checkout/components/CheckoutCart';
import { CheckoutCartSuccessModal } from '../../../../../services/checkout/components/CheckoutCartSuccess';
import { CHECKOUT_CART_MODAL } from '../../../../../services/checkout/components/CheckoutCart/modal';
import { CHECKOUT_CART_SUCCESS_MODAL } from '../../../../../services/checkout/components/CheckoutCartSuccess/modal';
import {
  openModal as openModalAction,
  closeModal as closeModalAction,
} from '../../../../../actions/modal-actions';
import { isPictureDayMembership } from './helpers';
import { PurchasesPendingConfigurationModal } from '../../../../pro/views/HierarchyItem/Matches/components/PurchasesPendingConfiguration';
import PurchasedPrintPackageConfigurationCompleteModal from '../../../../pro/views/HierarchyItem/Matches/components/PurchasesPendingConfiguration/components/PurchasedPrintPackageConfigurationCompleteModal';
import useHierarchyViewsCreate from '../../../../../components/global/HiearchyGroups/hooks/useHierarchyViewsCreate';
import { HierarchyItemForMatches } from '../../../../pro/views/HierarchyItem/Matches/graphql/queries/hierarchy-item';
import { supportsAutoJoinNullState } from '../../../../pro/views/HierarchyItem/helpers';
import AutoJoinNullState from '../../../../pro/views/HierarchyItem/components/AutoJoinNullState';
import RouteSelector from '../../../../pro/views/HierarchyItem/components/HierarchyItemHeader/components/RouteSelector';
import {
  clearAlbumMembershipGroupFilters as clearAlbumMembershipGroupFiltersAction,
  setPendingPurchasingOption as setPendingPurchaseOptionAction,
} from '../../../redux/memberships/membershipsActions';
import FullScreenLoader from '../../../../../components/global/FullScreenLoader';
import useDismissiblePersistent from '../../../../../hooks/useDismissiblePersistent';
import { CONSUMER_VIEW_APP_DOWNLOAD_BANNER } from '../../../../../constants/dismissibleKeys';
import { ALBUM_SUBTYPE, ALBUM_TYPE } from '../../../../../constants/albums';
import { isMobile } from '../../../../../util/device-detector';
import AppDownloadBanner from '../../../../../components/global/AppDownloadBanner';
import { isPhotoFinderCreditsDeliveryEnabled } from '../../../../../util/album-folder';

const propTypes = {
  account: PropTypes.shape({
    id: PropTypes.string,
  }),
  albumMembershipGroup: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }),
  openModal: PropTypes.func,
  closeModal: PropTypes.func,
  client: PropTypes.shape({
    resetStore: PropTypes.func,
  }),
  route: PropTypes.shape({
    routes: PropTypes.arrayOf(
      PropTypes.shape({
        path: PropTypes.string,
        exact: PropTypes.bool,
        component: PropTypes.node,
      }),
    ),
  }),
  match: PropTypes.shape({
    params: PropTypes.shape({
      albumMembershipGroupId: PropTypes.string,
    }),
  }),
  history: PropTypes.shape({
    replace: PropTypes.func,
    push: PropTypes.func,
  }),
  location: PropTypes.shape({
    pathname: PropTypes.string,
  }),
  albumFolder: PropTypes.shape({
    id: PropTypes.string,
  }),
  clearAlbumMembershipGroupFilters: PropTypes.func,
};

const AlbumMembershipGroup = ({
  account,
  albumMembershipGroup,
  client,
  openModal,
  closeModal,
  route,
  match,
  history,
  location,
  albumFolder,
  clearAlbumMembershipGroupFilters,
  setPendingPurchaseOption,
}) => {
  const { _branch_match_id: branchMatchId } = qs.parse(location?.search);
  const [fromBranchLink] = useState(!!branchMatchId);

  const albumMembershipGroupId =
    albumMembershipGroup?.id ?? match?.params?.albumMembershipGroupId;

  const entity =
    albumMembershipGroup?.album_folder ??
    albumMembershipGroup?.album ??
    albumFolder;

  const {
    onDismiss: onDismissAppDownloadBanner,
    visible: downloadBannerVisible,
  } = useDismissiblePersistent(CONSUMER_VIEW_APP_DOWNLOAD_BANNER);

  // AlbumFolder is used for the items below since it has the proper data.
  const appDownloadBannerVisible =
    isMobile() &&
    downloadBannerVisible &&
    !albumFolder?.picture_day_enabled &&
    (isPhotoFinderCreditsDeliveryEnabled(albumFolder) ||
      albumFolder?.album_subtype === ALBUM_SUBTYPE.EVENTS ||
      [ALBUM_TYPE.EVENT, ALBUM_TYPE.SHUTTERBUG, ALBUM_TYPE.CONSUMER].includes(
        albumFolder?.album_type,
      ));

  useHierarchyViewsCreate({ recordId: albumMembershipGroupId });

  useEffect(() => {
    if (
      location.pathname === `/consumer/matches/${albumMembershipGroup?.id}/all`
    ) {
      setPendingPurchaseOption?.(null);
      clearAlbumMembershipGroupFilters?.();
    }
  }, [location?.pathname, albumMembershipGroup?.id]);

  useEffect(() => {
    if (albumMembershipGroup && !fromBranchLink) {
      const isPictureDay = isPictureDayMembership(albumMembershipGroup);

      if (!isPictureDay) {
        const isAllPhotosUrl =
          location.pathname ===
          `/consumer/matches/${albumMembershipGroup?.id}/all`;

        history.replace(
          `/app/shutterbug/hierarchy-items/${albumMembershipGroup?.id}${
            isAllPhotosUrl ? '' : '/matches'
          }`,
        );
      }
    }
  }, [albumMembershipGroup, fromBranchLink]);

  if (!account) {
    return null;
  }

  const displayAutoJoinNullState = supportsAutoJoinNullState(albumFolder);

  if (!entity) {
    return (
      <Fragment>
        <Helmet>
          <title>View your photos</title>
          {/* Intends to replace any class added by another view and prevent errors from redirects */}
          <body className="body-consumer-matches" />
        </Helmet>
        <FullScreenLoader />
      </Fragment>
    );
  }

  return (
    <Fragment>
      <Helmet>
        <title>View your photos</title>
        {/* Intends to replace any class added by another view and prevent errors from redirects */}
        <body className="body-consumer-matches" />
      </Helmet>

      <div className="sticky-top">
        <Header albumMembershipGroup={albumMembershipGroup} />
        {!displayAutoJoinNullState && <IdentitySelector />}
        <RouteSelector
          hierarchyItem={entity}
          albumMembershipGroup={albumMembershipGroup}
          className="pt-1 pb-0 px-md-2 mt-0"
          isConsumerView
          dropdownEnabled
        />
      </div>

      <PurchasedCustomPrintConfigNotice />
      <CheckoutCartModal
        albumFolderId={albumFolder?.id}
        onSuccess={() => {
          // We gotta nuke everything until we can find a way to clear
          // only the relevant data that need clearing (i.e. AlbumMembershipGroup
          // and purchases, purchased custom prints, etc).
          client.resetStore();
          closeModal(CHECKOUT_CART_MODAL);
          openModal(CHECKOUT_CART_SUCCESS_MODAL);
        }}
      />
      <CheckoutCartSuccessModal
        onButtonClick={() => closeModal(CHECKOUT_CART_SUCCESS_MODAL)}
      />
      <PurchasesPendingConfigurationModal
        hierarchyItemId={
          albumMembershipGroup?.is_album
            ? albumMembershipGroup.album.root_album_folder?.id
            : albumMembershipGroup?.id ?? match?.params?.albumMembershipGroupId
        }
        onCheckout={() => {
          openModal(CHECKOUT_CART_MODAL);
        }}
      />
      <PurchasedPrintPackageConfigurationCompleteModal
        hierarchyItemId={
          albumMembershipGroup?.is_album
            ? albumMembershipGroup.album.root_album_folder?.id
            : albumMembershipGroup?.id
        }
        onCheckout={() => {
          openModal(CHECKOUT_CART_MODAL);
        }}
      />
      {displayAutoJoinNullState ? (
        <AutoJoinNullState className="mt-5" entity={albumFolder} />
      ) : (
        <>{renderRoutes(route.routes)}</>
      )}
      {appDownloadBannerVisible && (
        <AppDownloadBanner onDismiss={onDismissAppDownloadBanner} />
      )}
    </Fragment>
  );
};

AlbumMembershipGroup.propTypes = propTypes;

const mapDispatchToProps = {
  openModal: openModalAction,
  closeModal: closeModalAction,
  setPendingPurchaseOption: setPendingPurchaseOptionAction,
  clearAlbumMembershipGroupFilters: clearAlbumMembershipGroupFiltersAction,
};

const withHierarchyItem = graphql(HierarchyItemForMatches, {
  options: ({ match }) => ({
    variables: {
      id: match.params.hierarchyItemId ?? match.params.albumMembershipGroupId,
    },
    fetchPolicy: 'cache-first',
    ssr: false,
  }),
  props: ({ data: { hierarchyItem, loading } }) => ({
    albumFolder:
      hierarchyItem?.album_folder ?? hierarchyItem?.album?.root_album_folder,
    loadingHierarchyItem: loading,
  }),
});

const withAlbumMembershipGroup = graphql(
  getSimplifiedAlbumMembershipGroupQuery,
  {
    options: ({ albumMembershipGroupId }) => ({
      variables: {
        id: albumMembershipGroupId,
      },
      fetchPolicy: 'cache-first',
      ssr: false,
    }),
    props: ({ data: { albumMembershipGroup, loading } }) => ({
      albumMembershipGroup,
      loadingAlbumMembershipGroup: loading,
    }),
    skip: ({ albumMembershipGroupId }) => !albumMembershipGroupId,
  },
);

export default compose(
  withRouter,
  withApollo,
  connect(null, mapDispatchToProps),
  getAccount,
  mapProps(({ data, ...props }) => ({
    ...props,
    albumMembershipGroupId: props.match.params.albumMembershipGroupId,
    account: data.me,
  })),
  withHierarchyItem,
  withAlbumMembershipGroup,
)(AlbumMembershipGroup);
